import { useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { parseUnits } from 'viem'

import { EarnFormValues } from '~/components/forms/earn/earnSchema'
import {
  useGetSwapLoggingData,
  useSwapRequest,
} from '~/components/forms/swap/hooks/useSwap'
import { SwapDirection } from '~/components/forms/swap/utils'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  EarnTransactionDepositParams,
  EarnTransactionWithdrawParams,
} from '~/hooks/smart-account-actions/types'
import { useCreateEarnTransaction } from '~/hooks/smart-account-actions/useCreateEarnTransaction'
import { TokenMetadata } from '~/hooks/useTokenMetadata'
import {
  BackendLoggingData,
  EarnAction,
  IAddress,
  OrgSmartAccount,
  OrgTransaction,
} from '~/types'
import { getAutoEarnModule } from '~/utils/vault'

export const useCreateEarnTransactionSubmit = ({
  activeOrgId,
  setIsOpen,
  assetToken,
  vaultToken,
  sellToken,
}: {
  activeOrgId?: string
  setIsOpen: (isOpen: boolean) => void
  assetToken?: TokenMetadata
  vaultToken?: TokenMetadata
  sellToken?: TokenMetadata
}) => {
  const queryClient = useQueryClient()
  const { createEarnTransaction, isProcessing: isProcessingTransaction } =
    useCreateEarnTransaction()

  const createTransactionOnSubmit = useCallback(
    (
      dontOpenDialog: boolean,
      callback?: (transaction: OrgTransaction) => void,
    ) =>
      async ({
        sender,
        data,
        loggingData,
      }: {
        sender: OrgSmartAccount
        data: EarnFormValues
        loggingData?: BackendLoggingData
      }) => {
        const isDeposit = data.action === EarnAction.DEPOSIT

        if (!assetToken || !vaultToken) {
          throw new Error('Token metadata is required')
        }

        const baseParams = {
          smartAccountAddress: data.account,
          smartAccountId: sender.id,
          assets: parseUnits(data.asset.amount, assetToken.decimals),
          calls: data.calls,
          assetToken: {
            address: assetToken.address,
            decimals: assetToken.decimals,
            symbol: assetToken.symbol,
          },
          vaultToken: {
            address: vaultToken.address,
            decimals: vaultToken.decimals,
            symbol: vaultToken.symbol,
          },
          chainId: data.asset.chainId,
          shares: parseUnits(data.vault.amount, vaultToken.decimals),
          memo: data.memo,
          routerType: data.swapData?.swapRouter ?? 'SplitsVaultRouter',
          loggingData,
        }

        let transactionParams:
          EarnTransactionDepositParams | EarnTransactionWithdrawParams

        if (
          isDeposit &&
          data.swapData &&
          sellToken &&
          data.swapData.sellToken
        ) {
          transactionParams = {
            ...baseParams,
            action: EarnAction.DEPOSIT,
            sourceAmount: parseUnits(
              data.swapData.sellToken.amount,
              sellToken.decimals,
            ),
            sourceToken: {
              address: sellToken.address,
              decimals: sellToken.decimals,
              symbol: sellToken.symbol,
            },
            provider: data.swapData.swapRouter,
          }
        } else if (!isDeposit) {
          transactionParams = {
            ...baseParams,
            action: EarnAction.WITHDRAW,
            // Only where the withdraw form shows the pause field.
            pauseAutoEarnSeconds: getAutoEarnModule(sender, data.asset.chainId)
              ? data.pauseAutoEarnSeconds
              : undefined,
          }
        } else {
          throw new Error('Invalid form data')
        }

        await createEarnTransaction(
          transactionParams,
          dontOpenDialog,
          (transaction) => {
            queryClient.setQueryData(
              [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
              transaction,
            )
            setIsOpen(false)
            callback?.(transaction)
          },
        )
      },

    [
      assetToken,
      vaultToken,
      sellToken,
      createEarnTransaction,
      queryClient,
      activeOrgId,
      setIsOpen,
    ],
  )

  return {
    createTransactionOnSubmit,
    isProcessingTransaction,
  }
}

export const useEarnRequest = ({
  account,
  sellToken,
  sellAmount,
  sellChainId,
  vaultToken,
  vaultChainId,
  vaultAmount,
  assetToken,
  assetAmount,
  assetChainId,
  action,
}: {
  account: IAddress
  sellToken: IAddress
  sellAmount: string
  sellChainId: SupportedChainId
  vaultToken: IAddress
  vaultChainId: SupportedChainId
  vaultAmount: string
  assetToken: IAddress
  assetAmount: string
  assetChainId: SupportedChainId
  action: EarnAction
}): Parameters<typeof useSwapRequest>[0] => {
  return useMemo(() => {
    const isDeposit = action === EarnAction.DEPOSIT

    if (isDeposit) {
      return {
        account: account,
        sourceChainId: sellChainId,
        sourceToken: sellToken,
        sourceAmount: sellAmount,
        destinationToken: vaultToken,
        destinationChainId: vaultChainId,
        destinationAmount: vaultAmount,
        swapDirection: SwapDirection.SELL,
      }
    } else {
      return {
        account: account,
        sourceChainId: vaultChainId,
        sourceToken: vaultToken,
        sourceAmount: vaultAmount,
        destinationToken: assetToken,
        destinationChainId: assetChainId,
        destinationAmount: assetAmount,
        swapDirection: SwapDirection.BUY,
      }
    }
  }, [
    account,
    sellToken,
    sellAmount,
    sellChainId,
    vaultToken,
    vaultChainId,
    vaultAmount,
    assetToken,
    assetAmount,
    assetChainId,
    action,
  ])
}

export const useEarnLoggingData = ({
  account,
  sellToken,
  sellAmount,
  sellChainId,
  vaultToken,
  vaultChainId,
  vaultAmount,
  assetToken,
  assetAmount,
  assetChainId,
  action,
}: {
  account: IAddress
  sellToken: IAddress
  sellAmount: string
  sellChainId: SupportedChainId
  vaultToken: IAddress
  vaultChainId: SupportedChainId
  vaultAmount: string
  assetToken: IAddress
  assetAmount: string
  assetChainId: SupportedChainId
  action: EarnAction
}): Parameters<typeof useGetSwapLoggingData>[0] => {
  return useMemo(() => {
    const isDeposit = action === EarnAction.DEPOSIT

    if (isDeposit) {
      return {
        sellToken,
        sellAmount,
        sellChainId,
        buyToken: vaultToken,
        buyChainId: vaultChainId,
        buyAmount: vaultAmount,
        sender: account,
        recipients: [{ address: account }],
      }
    } else {
      return {
        sellToken: vaultToken,
        sellAmount: vaultAmount,
        sellChainId: vaultChainId,
        buyToken: assetToken,
        buyChainId: assetChainId,
        buyAmount: assetAmount,
        sender: account,
        recipients: [{ address: account }],
      }
    }
  }, [
    action,
    sellToken,
    sellAmount,
    sellChainId,
    vaultToken,
    vaultChainId,
    vaultAmount,
    account,
    assetToken,
    assetAmount,
    assetChainId,
  ])
}

export const useEarnForm = ({
  defaultAccount,
  defaultAmount = '',
  defaultMemo = '',
  assetToken: defaultAssetToken,
  vaultToken: defaultVaultToken,
  chainId,
  action,
}: {
  defaultAccount: IAddress | undefined
  /** Decimal string in the asset token's units, e.g. "10000" */
  defaultAmount?: string
  defaultMemo?: string
  assetToken: IAddress
  vaultToken: IAddress
  chainId: SupportedChainId
  action: EarnAction
}) => {
  const methods = useForm<EarnFormValues>({
    mode: 'onChange',
    defaultValues: {
      account: defaultAccount,
      asset: {
        token: defaultAssetToken,
        amount: '',
        chainId: chainId,
      },
      vault: {
        token: defaultVaultToken,
        amount: '',
        chainId: chainId,
      },
      action: action,
      pauseAutoEarnSeconds: 300, // 5 minutes
      swapData:
        action === EarnAction.DEPOSIT
          ? {
              sellToken: {
                token: defaultAssetToken,
                amount: defaultAmount,
                chainId: chainId,
              },
              providerTransactionId: '',
              swapRouter: 'SplitsVaultRouter',
            }
          : undefined,
      calls: [],
      memo: defaultMemo,
    },
  })

  return methods
}
